import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { Icon, IconStyle } from '@gitbook/icons';
import { Picker, type PickerItemData } from '@gitbook/react-uikit';
import { FieldGroup, Group, Label } from '@gitbook/spine-react';

import { useUpgradeSite } from '../../../state';
import { getPaidIndicatorBadgeProps, PaidFeatureIndicator } from '../../Site';
import type { SitesCustomizationPanelProps } from '../types';

// @site-icons - Keep it synced with backend by grepping that tag.

/**
 * Settings to configure the style for icons
 */
export function ThemeIconsSettings(props: SitesCustomizationPanelProps): React.ReactElement {
    const { site, settings, setSettings } = props;
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesAdvancedCustomization);

    const options: PickerItemData<api.CustomizationIconsStyle>[] = [
        {
            value: api.CustomizationIconsStyle.Solid,
            label: 'Solid',
            icon: <IconsStyleIcon value={api.CustomizationIconsStyle.Solid} />,
        },
        {
            value: api.CustomizationIconsStyle.Duotone,
            label: 'Duotone',
            icon: <IconsStyleIcon value={api.CustomizationIconsStyle.Duotone} />,
        },
        {
            value: api.CustomizationIconsStyle.Regular,
            label: 'Regular',
            icon: <IconsStyleIcon value={api.CustomizationIconsStyle.Regular} />,
        },
        {
            value: api.CustomizationIconsStyle.Light,
            label: 'Light',
            icon: <IconsStyleIcon value={api.CustomizationIconsStyle.Light} />,
        },
        {
            value: api.CustomizationIconsStyle.Thin,
            label: 'Thin',
            icon: <IconsStyleIcon value={api.CustomizationIconsStyle.Thin} />,
        },
    ].map((option) => {
        const isFreeIcon = option.value === api.CustomizationIconsStyle.Regular;

        return {
            ...option,
            upgrade: !isFreeIcon,
            badge: getPaidIndicatorBadgeProps({
                upgrade: isFreeIcon ? null : upgrade,
                isFeatureActive: settings.styling.icons === option.value,
            }),
        };
    });

    return (
        <FieldGroup orientation="horizontal">
            <Group>
                <Label>Icons</Label>
                <PaidFeatureIndicator
                    upgrade={upgrade}
                    isFeatureActive={settings.styling.icons !== api.CustomizationIconsStyle.Regular}
                />
            </Group>
            <Picker
                value={settings.styling.icons}
                items={options}
                onValueChange={(value) => {
                    setSettings((prev) => ({
                        ...prev,
                        styling: {
                            ...prev.styling,
                            icons: value,
                        },
                    }));
                }}
            />
        </FieldGroup>
    );
}

const styleMap: {
    [key in api.CustomizationIconsStyle]: IconStyle;
} = {
    [api.CustomizationIconsStyle.Solid]: IconStyle.Solid,
    [api.CustomizationIconsStyle.Duotone]: IconStyle.Duotone,
    [api.CustomizationIconsStyle.Regular]: IconStyle.Regular,
    [api.CustomizationIconsStyle.Light]: IconStyle.Light,
    [api.CustomizationIconsStyle.Thin]: IconStyle.Thin,
};

/**
 * Render the icon for the selected style
 */
function IconsStyleIcon(props: {
    value: api.CustomizationIconsStyle;
    size?: number;
    className?: string;
}): React.ReactElement {
    const { value, className, size } = props;

    return <Icon icon="palette" iconStyle={styleMap[value]} size={size} className={className} />;
}
